import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient, normalizeUser } from '@/services/supabase';
import { toast } from 'react-toastify';
import { api } from '@/services/api';
import { secureKeyService } from '@/services/secure-key.service';

export default function AuthCallback() {
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Extract the code from the URL
    const handleCallback = async () => {
      try {
        // Get URL parameters
        const { searchParams } = new URL(window.location.href);
        const code = searchParams.get('code');
        const next = searchParams.get('next') ?? '/dashboard';
        const validateParam = searchParams.get('validate');
        
        if (!code) {
          throw new Error('No code provided in callback URL');
        }
        
        console.log('Exchanging code for session...');
        
        // Exchange the code for a session - this is the critical step
        // This is handled by Supabase and uses the GitHub client ID and secret
        // configured in your Supabase project
        const supabase = createClient();
        const { data, error } = await (await supabase).auth.exchangeCodeForSession(code);
        
        if (error) {
          throw error;
        }
        
        console.log('Session established:', data?.session != null);
        
        // Get the user details after successful authentication
        const { data: userData, error: userError } = await (await supabase).auth.getUser();
        
        if (userError) {
          throw userError;
        }
        
        const user = userData.user;
        
        if (!user) {
          throw new Error('Failed to get user information');
        }
        
        // Use normalizeUser to create a consistent user object
        console.debug('Using normalizeUser in Callback handleCallback');
        const normalizedUser = normalizeUser(user);
        console.debug('Normalized user:', normalizedUser.role, normalizedUser.business_id);
        
        // Get Supabase API key from secure key service
        
        // Get the authorized admin email from secure key service
        const ADMIN_EMAIL = secureKeyService.getKey('ADMIN_EMAIL');

        // If admin email is configured, verify this is an authorized super admin
        if (ADMIN_EMAIL && user.email !== ADMIN_EMAIL) {
          // This GitHub user is not authorized as a super admin
          toast.error('This GitHub account is not authorized as a super admin');
          await (await supabase).auth.signOut();
          navigate('/super-login');
          return;
        }
        
        // If no admin email is configured, show a warning but continue
        if (!ADMIN_EMAIL) {
          console.warn('Admin email not configured, allowing GitHub authentication without verification');
          toast.warning('Admin verification is not configured. Proceeding with limited validation.');
        }
        
        // Now that we have GitHub authentication, validate with our backend
        try {
          // Call the backend API to validate the GitHub authentication
          const response = await api.post('/api/auth/v1/business/token', {
            email: user.email,
            // Use a special grant type to indicate GitHub auth
            grant_type: 'github',
            // Include the GitHub token for verification
            github_token: data.session?.access_token
          });
          
          if (response.data?.access_token) {
            // Create a session object that aligns with our backend token format
            
            
            
            console.log('Backend authentication successful');
          } else {
            throw new Error('Failed to authenticate with backend');
          }
        } catch (backendError) {
          console.error('Backend authentication error:', backendError);
          
          // Fallback to direct session if backend auth fails
          // This is a temporary measure until backend GitHub auth is fully implemented
          const sessionData = {
            access_token: data.session?.access_token || user.id,
            token_type: 'bearer',
            user: {
              id: user.id, 
              email: user.email,
              role: 'super_admin', // Only super admins use GitHub auth
              business_name: 'Catch of the Day Admin',
              user_metadata: {
                provider: 'github',
                role: 'super_admin',
                business_name: 'Catch of the Day Admin'
              }
            },
            expires_at: Date.now() + (3600 * 1000) // 1 hour expiration
          };
          
          
          
          // Set up API headers
          api.defaults.headers.common['Authorization'] = `Bearer ${sessionData.access_token}`;
        }
        
        // Success notification
        toast.success('Successfully authenticated with GitHub');
        
        // Redirect based on the callback context
        if (validateParam === 'admin') {
          navigate('/super-register'); // Redirect to super register page after GitHub validation
        } else {
          navigate(next); // Otherwise redirect to the specified next page or dashboard
        }
      } catch (err: any) {
        console.error('Error in GitHub auth callback:', err);
        setError(err.message || 'Failed to authenticate with GitHub');
        toast.error('Authentication failed: ' + (err.message || 'Unknown error'));
        setTimeout(() => navigate('/auth/auth-code-error'), 3000);
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full px-6 py-8 bg-white shadow-md rounded-lg">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            {loading ? 'Processing Authentication...' : error ? 'Authentication Error' : 'Successfully Authenticated'}
          </h2>
          
          {loading && (
            <div className="flex justify-center my-4">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-sky-600"></div>
            </div>
          )}
          
          {error && (
            <div className="bg-red-50 text-red-700 p-4 rounded-md mt-4 text-sm">
              {error}
              <p className="mt-2">Redirecting you to error page...</p>
            </div>
          )}
          
          {!loading && !error && (
            <div className="bg-green-50 text-green-700 p-4 rounded-md mt-4 text-sm">
              Successfully authenticated with GitHub. Redirecting you...
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
